import { useCallback, useContext, useLayoutEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'
import { selectUnsentAgentV2Chat } from '@/agent-v2/conversations/store/selectors'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { AuthContext } from '@/context/auth-context'
import { kRouteSegments } from '@/routing/paths'
import type { RootState } from '@/store/store'

/**
 * Starts a new chat for shared entry points. Returns to the viewer's unsent chat in the matter
 * when there is one, so an in-progress chat isn't replaced by another empty one.
 */
export function useCreateNewChat() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Read at click time, so the callback keeps its identity across navigations.
  const pathnameRef = useRef(pathname)
  useLayoutEffect(() => {
    pathnameRef.current = pathname
  })
  const store = useStore<RootState>()
  const { user } = useContext(AuthContext)
  const currentUserId = user?.uid
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()

  const createNewChat = useCallback(
    (navigateOnSuccess: boolean, matterId: string, onSuccess?: (created: boolean) => void, onError?: (e: unknown) => void) => {
      if (navigateOnSuccess) {
        const unsentChat = selectUnsentAgentV2Chat(store.getState(), { matterId, currentUserId })
        if (unsentChat) {
          const unsentChatPath = buildConversationPath(matterId, unsentChat.id, kRouteSegments.agentV2)
          if (pathnameRef.current !== unsentChatPath) navigate(unsentChatPath)
          onSuccess?.(false)
          return
        }
      }

      void createNewAgentV2Chat(navigateOnSuccess, matterId, onSuccess && (() => onSuccess(true)), onError)
    },
    [createNewAgentV2Chat, currentUserId, navigate, store]
  )

  return { createNewChat }
}
